import React, { useState } from 'react';
import { Box, Grid, Typography, CardContent, Chip, Button, InputBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Search as SearchIcon } from '@mui/icons-material';
import GradientText from './common/GradientText.tsx';
import HolographicBackground from './common/HolographicBackground.tsx';
import HolographicContainer from './common/HolographicContainer.tsx';
import SurrogateAvatar from './common/SurrogateAvatar.tsx';
import { getAvatarComponent } from '../utils/getAvatarComponent.tsx';

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  maxWidth: '800px',
  margin: '0 auto 40px',
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(2),
  padding: '1.4rem 2rem',
  border: '1px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    border: '1px solid rgba(0, 255, 255, 0.3)',
    boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  }
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  width: '100%',
  color: '#BFD2FF',
  fontSize: '1.2rem',
  '&::placeholder': {
    color: '#7881A1',
  }
}));

const AgentCard = styled(motion(HolographicContainer))(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiCardContent-root': {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  }
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  background: 'rgba(138, 43, 226, 0.1)',
  border: '1px solid rgba(138, 43, 226, 0.2)',
  color: '#8a2be2',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(0, 255, 255, 0.1)',
    border: '1px solid rgba(0, 255, 255, 0.3)',
    color: '#00ffff',
    boxShadow: '0 0 10px rgba(0, 255, 255, 0.3)',
  }
}));

const ConnectButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #003366 30%, #8A2BE2 90%)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #00CC99 30%, #00ffff 90%)',
    boxShadow: '0 0 15px rgba(0, 255, 255, 0.5)',
  }
}));

const surrogates = [
  {
    id: '1',
    name: 'Alpha',
    role: 'Assistant',
    image: 'Alpha',
    specialty: 'Natural Language Processing',
    status: 'active',
    rating: 4.8,
    completedTasks: 1234
  },
  {
    id: '2',
    name: 'Beta',
    role: 'Researcher',
    image: 'Beta',
    specialty: 'Data Analysis',
    status: 'active',
    rating: 4.6,
    completedTasks: 987
  },
  {
    id: '3',
    name: 'Gamma',
    role: 'Developer',
    image: 'Gamma',
    specialty: 'Code Generation',
    status: 'active',
    rating: 4.9,
    completedTasks: 2341
  },
  {
    id: '4',
    name: 'Delta',
    role: 'Strategist',
    image: 'Delta',
    specialty: 'Business Planning',
    status: 'active',
    rating: 4.7,
    completedTasks: 1567
  }
];

const MarketplaceHome: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get the VoiceIcon component
  const VoiceIcon = getAvatarComponent('VoiceIcon', '#00CC99', 24);

  return (
    <HolographicBackground>
      <Box sx={{ p: 4 }}>
        <GradientText variant="h4" gradient="primary" sx={{ textAlign: 'center', mb: 4 }}>
          AI Surrogate Marketplace
        </GradientText>

        <SearchContainer>
          <SearchInput
            placeholder="Search for AI surrogates, specialties, or capabilities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 2,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              }
            }}
          >
            {VoiceIcon}
          </Box>
          <SearchIcon sx={{ 
            color: '#7881A1', 
            cursor: 'pointer', 
            ml: 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              color: '#00ffff',
              transform: 'scale(1.1)',
            }
          }} />
        </SearchContainer>

        <Grid container spacing={4}>
          {surrogates.map((surrogate, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={surrogate.id}>
              <AgentCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <SurrogateAvatar
                      name={surrogate.name}
                      role={surrogate.role}
                      image={surrogate.image}
                      status={surrogate.status as 'active' | 'idle' | 'busy'}
                      size="medium"
                      showName={true}
                    />
                  </Box>

                  <StyledChip label={surrogate.specialty} size="small" sx={{ mb: 2 }} />

                  <Box sx={{ mb: 2, flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.6)',
                      transition: 'all 0.3s ease',
                      '&:hover': { color: 'rgba(255, 255, 255, 0.9)' }
                    }}>
                      Rating: {surrogate.rating}/5.0
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.6)',
                      transition: 'all 0.3s ease',
                      '&:hover': { color: 'rgba(255, 255, 255, 0.9)' }
                    }}>
                      Tasks: {surrogate.completedTasks}
                    </Typography>
                  </Box>

                  <ConnectButton variant="contained" fullWidth>
                    Connect
                  </ConnectButton>
                </CardContent>
              </AgentCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    </HolographicBackground>
  );
};

export default MarketplaceHome; 